import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Check, Eye, EyeOff, ShieldCheck } from "lucide-react";
import affiniaLogo from "@/assets/affinia-logo.png";
import affiniaBg from "@/assets/affinia-bg.png";
import { changePassword, getUser, isAuthenticated, markActive } from "@/services/userLogin";

const RULES = [
  // Must not be looser than the API's ChangePasswordRequest validator, or the
  // form accepts a password the server then rejects with a 422.
  { label: "At least 12 characters", test: (v: string) => v.length >= 12 },
  { label: "One uppercase letter", test: (v: string) => /[A-Z]/.test(v) },
  { label: "One lowercase letter", test: (v: string) => /[a-z]/.test(v) },
  { label: "One number", test: (v: string) => /\d/.test(v) },
];

const SetupPassword = () => {
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!isAuthenticated()) {
      navigate("/", { replace: true });
    } else if (!getUser()?.must_change_password) {
      navigate("/dashboard", { replace: true });
    }
  }, [navigate]);

  const rulesPass = RULES.every((r) => r.test(password));
  const matches = password.length > 0 && password === confirm;
  const canSubmit = rulesPass && matches && !submitting;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await changePassword(password);
      markActive();
      navigate("/dashboard", { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to update password");
      setSubmitting(false);
    }
  };

  return (
    <div
      className="light min-h-dvh flex items-center justify-center bg-cover bg-center bg-no-repeat px-4 py-8 sm:px-6 text-foreground"
      style={{ backgroundImage: `url(${affiniaBg})` }}
    >
      <Card className="w-full max-w-md shadow-2xl bg-white/95 backdrop-blur-sm border-0">
        <CardHeader className="space-y-4 pb-4">
          <div className="flex justify-center">
            <img src={affiniaLogo} alt="Affinia" className="h-28" />
          </div>
          <div className="text-center space-y-1">
            <div className="flex items-center justify-center gap-2">
              <ShieldCheck className="h-5 w-5 text-primary" aria-hidden />
              <h1 className="text-lg font-semibold text-foreground">Set your password</h1>
            </div>
            <p className="text-sm text-muted-foreground">
              You signed in with a temporary password. Choose a new one to continue.
            </p>
          </div>
        </CardHeader>

        <CardContent>
          <form className="space-y-4" onSubmit={handleSubmit}>
            <div className="space-y-1.5">
              <Label htmlFor="new-password">New password</Label>
              <div className="relative">
                <Input
                  id="new-password"
                  type={show ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="new-password"
                  autoFocus
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShow((s) => !s)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  aria-label={show ? "Hide password" : "Show password"}
                >
                  {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="confirm-password">Confirm password</Label>
              <Input
                id="confirm-password"
                type={show ? "text" : "password"}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                autoComplete="new-password"
              />
              {confirm.length > 0 && !matches && (
                <p className="text-xs text-destructive">Passwords don’t match.</p>
              )}
            </div>

            <ul className="space-y-1">
              {RULES.map((rule) => {
                const ok = rule.test(password);
                return (
                  <li
                    key={rule.label}
                    className={`flex items-center gap-2 text-xs ${
                      ok ? "text-foreground" : "text-muted-foreground"
                    }`}
                  >
                    <Check
                      className={`h-3.5 w-3.5 ${ok ? "text-primary" : "text-muted-foreground/50"}`}
                      aria-hidden
                    />
                    {rule.label}
                  </li>
                );
              })}
            </ul>

            {error && <p className="text-sm text-destructive">{error}</p>}

            <Button type="submit" className="w-full" disabled={!canSubmit}>
              {submitting ? "Saving…" : "Set password & continue"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default SetupPassword;
